/**
 * Renders assistant chat text with a minimal, sanitised subset of markdown
 * (bold, italic, inline code, bullet/numbered lists, paragraphs, explicit links).
 *
 * Bare URLs are NOT autolinked — only explicit `[label](href)` markdown links
 * become anchors. Internal links (starting with `/`) are intercepted at click
 * time and routed via React Router + the session guard.
 */
import { useMemo, type MouseEvent } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { useSessionGuard } from '@/shared/hooks/useSessionGuard';

marked.use({ gfm: false, breaks: false });

const ALLOWED_TAGS = [
  'p',
  'strong',
  'em',
  'code',
  'pre',
  'ul',
  'ol',
  'li',
  'a',
  'br',
  'blockquote',
];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node instanceof HTMLAnchorElement) {
    const href = node.getAttribute('href');
    if (href && !href.startsWith('/')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  }
});

export function renderChatMarkdown(text: string): string {
  const raw = marked.parse(text, { async: false });
  return DOMPurify.sanitize(raw, { ALLOWED_TAGS, ALLOWED_ATTR });
}

export function ChatMessageText({ text }: { text: string }) {
  const navigate = useNavigate();
  const { guardSession } = useSessionGuard();

  const html = useMemo(() => renderChatMarkdown(text), [text]);

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href || !href.startsWith('/')) return;

    e.preventDefault();
    const lessonMatch = href.match(/\/(?:courses\/[^/]+\/|lessons\/)([a-f0-9-]+)/);
    const lessonId = lessonMatch?.[1];
    if (lessonId) {
      guardSession({ lessonId, onProceed: () => navigate(href) });
    } else {
      navigate(href);
    }
  };

  return (
    <div
      className="f-chat-md f-prompt-prose"
      onClick={handleClick}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
